import React, { memo, useMemo } from "react";
import moment from "moment";
import {
  HiOutlineClipboardDocumentList,
  HiOutlineTruck,
} from "react-icons/hi2";
import {
  capitalizeWords,
  durationMinus2Hours,
  formatAmount,
  parseBooleanVal,
  quantityFromBreakdown,
  rosterInvoiceLineTotal,
  timeToQuantity,
  truncateDecimal,
} from "Utils/utils";
import { formatBreakdownClaimTypeSuffix } from "pages/Rostering/WorkerShift/components/advanceModeSplitDefaults";
import {
  getBillingSplitMeta,
  getVisibleInvoiceLines,
  groupInvoiceLinesByParticipant,
  parseRatioAppliedDivisor,
  applyWorkerParticipantRatio,
  resolveBreakdownDefaultRatio,
  isV5MultiAssignmentTimesheet,
  isKmOptedIn,
  findParticipantTravelForParticipant,
  travelShareAmount,
  getParticipantTravelRows,
  getParticipantDisplayName,
  resolveShiftBillableTotal,
} from "pages/Rostering/UpdatedTimeSheet/TimeSheetDetailsSection/TabSection/NewCodeAllocation/v5BillingUtils";
import "./roster-invoice-breakdown.css";

const isBasicShiftLine = (line) =>
  !line?.ndis_support_item_number &&
  !line?.ndis_support_item_name &&
  (line?.invoice_price == null || line?.invoice_price === "");

/** Active-hours support lines are nested under sleepover parents with parent_id set. */
const isActiveHoursChildLine = (line) =>
  line?.parent_id != null && line?.is_sleepover === false;

const formatLineSchedule = (line) => {
  if (!line?.start_time && !line?.end_time) {
    return { date: "Time not recorded", hours: null };
  }

  const start = line?.start_time ? moment(line.start_time) : null;
  const end = line?.end_time ? moment(line.end_time) : null;

  if (start && end) {
    const sameDay = start.format("YYYY-MM-DD") === end.format("YYYY-MM-DD");
    return {
      date: sameDay
        ? start.format("ddd, D MMM YY")
        : `${start.format("ddd, D MMM")} – ${end.format("ddd, D MMM YY")}`,
      hours: `${start.format("hh:mm A")} – ${end.format("hh:mm A")}`,
    };
  }

  const single = start || end;
  return {
    date: single.format("ddd, D MMM YY"),
    hours: single.format("hh:mm A"),
  };
};

const getLineQuantity = (line, item) => {
  const isAdvance = isV5MultiAssignmentTimesheet(item);

  // Basic / basic-split Active Hours: bill the configured duration (same as
  // other hourly lines). Do not subtract 2h and do not use the sleepover window.
  if (!isAdvance && isActiveHoursChildLine(line) && line?.duration) {
    return formatAmount(timeToQuantity(line.duration, false));
  }

  if (isActiveHoursChildLine(line) && line?.duration) {
    return formatAmount(timeToQuantity(line.duration, false));
  }
  if (line?.start_time && line?.end_time) {
    return formatAmount(
      quantityFromBreakdown(line.start_time, line.end_time, line?.duration, {
        sleepover: line?.is_sleepover,
        isSta: line?.is_sta,
        parentId: line?.parent_id,
      })
    );
  }
  if (line?.duration) {
    return timeToQuantity(
      line?.parent_id != null ? durationMinus2Hours(line.duration) : line.duration,
      line?.parent_id != null ? false : Boolean(line?.is_sleepover || line?.is_sta)
    );
  }
  return null;
};

const parseApiLineTotal = (line) => {
  if (line?.total_amount == null || line?.total_amount === "") {
    return null;
  }
  const parsed = parseFloat(line.total_amount);
  return Number.isNaN(parsed) ? null : truncateDecimal(parsed);
};

/** Applied (per-participant) rate after ratio, matching Invoice Preview. */
const getLineRate = (line, ratio) => {
  if (line?.invoice_price == null || line?.invoice_price === "") {
    return null;
  }
  const applied = applyWorkerParticipantRatio(line.invoice_price, ratio);
  if (applied != null) {
    return truncateDecimal(applied);
  }
  const parsed = parseFloat(line.invoice_price);
  return Number.isNaN(parsed) ? null : truncateDecimal(parsed);
};

/**
 * Per-participant line total after ratio.
 * Prefer duration×rate calc (ratio-aware). Legacy API total_amount is stored
 * pre-ratio (qty × full catalogue rate) so it is divided when used as fallback;
 * V5 advance lines are serialized already prorated by their own split ratio and
 * must not be divided a second time.
 */
const getLineTotal = (line, ratio, isRatioAppliedApiTotal = false) => {
  const participantCount = parseRatioAppliedDivisor(ratio, 1);

  if (isActiveHoursChildLine(line)) {
    // Active hours bill the configured duration (Qty × Rate). The child's
    // start/end copy the sleepover window, so using those clocks multiplies
    // the rate by the full shift length. Ignore stored total_amount ($0 from
    // the old 2h rule) as well.
    if (line?.duration && line?.invoice_price) {
      return rosterInvoiceLineTotal(
        line.duration,
        line.invoice_price,
        participantCount,
        {
          sleepover: false,
          isSta: false,
          parentId: null,
          startTime: null,
          endTime: null,
        }
      );
    }
  } else if (line?.duration && line?.invoice_price) {
    return rosterInvoiceLineTotal(line.duration, line.invoice_price, participantCount, {
      sleepover: line?.is_sleepover,
      isSta: line?.is_sta,
      parentId: line?.parent_id,
      startTime: line?.start_time,
      endTime: line?.end_time,
    });
  }

  const apiTotal = parseApiLineTotal(line);
  if (apiTotal != null) {
    return isRatioAppliedApiTotal
      ? truncateDecimal(apiTotal)
      : truncateDecimal(apiTotal / parseFloat(participantCount || 1));
  }

  return null;
};

const formatMoney = (value) =>
  value != null && !Number.isNaN(Number(value))
    ? `$${formatAmount(value)}`
    : null;

const getLineRatio = (item, lineIndex, line) => {
  // The invoice line carries the ratio selected on the Advance split (e.g. 1:3).
  // Split metadata can fall back to 1:{people on the shift} when that column was
  // not loaded, which showed 1:1 for a single participant.
  if (line?.ratio != null && String(line.ratio).trim() !== "") {
    return String(line.ratio).trim();
  }

  const splitMeta = getBillingSplitMeta(item, lineIndex, line);
  const ratio = resolveBreakdownDefaultRatio(item, lineIndex, line, splitMeta);
  if (ratio) return ratio;

  const participantCount = item?.participants?.length ?? 0;
  if (participantCount > 1) return `1:${participantCount}`;

  return "1:1";
};

const sumGroupTotal = (lines = [], item) => {
  const ratioAppliedApiTotal = isV5MultiAssignmentTimesheet(item);

  return truncateDecimal(
    lines.reduce((sum, { line, lineIndex }) => {
      const ratio = getLineRatio(item, lineIndex, line);
      return sum + (getLineTotal(line, ratio, ratioAppliedApiTotal) ?? 0);
    }, 0)
  );
};

const resolveCreateInvoiceTravelTotal = (item) => {
  const kmTotal = parseFloat(item?.km_total);
  if (!Number.isNaN(kmTotal) && kmTotal > 0) {
    return kmTotal;
  }
  return 0;
};

/** Basic / basic-split: Shift Total = displayed line totals (includes Active Hours). Advance unchanged. */
export const resolveCreateInvoiceDisplayedShiftTotal = (item) => {
  if (isV5MultiAssignmentTimesheet(item)) {
    return resolveShiftBillableTotal(item);
  }

  const visible = getVisibleInvoiceLines(item);
  if (!visible.length) {
    return resolveShiftBillableTotal(item);
  }

  const lineSum = truncateDecimal(
    visible.reduce((sum, line, lineIndex) => {
      const ratio = getLineRatio(item, lineIndex, line);
      return sum + (getLineTotal(line, ratio, false) ?? 0);
    }, 0)
  );

  const travelTotal = resolveCreateInvoiceTravelTotal(item);
  return travelTotal > 0 ? truncateDecimal(lineSum + travelTotal) : lineSum;
};

const GridHead = memo(() => (
  <div className="rib-grid-head" role="row">
    <span role="columnheader">Time</span>
    <span role="columnheader">Support / service</span>
    <span role="columnheader">NDIS item</span>
    <span role="columnheader">Ratio</span>
    <span role="columnheader">Qty</span>
    <span role="columnheader">Rate</span>
    <span role="columnheader">Total</span>
  </div>
));

const LineRow = memo(({ line, lineIndex, item, showClaimTypeScheduleTimesheets }) => {
  const basic = isBasicShiftLine(line);
  const schedule = formatLineSchedule(line);
  const quantity = getLineQuantity(line, item);
  const ratio = getLineRatio(item, lineIndex, line);
  const total = getLineTotal(line, ratio, isV5MultiAssignmentTimesheet(item));
  const rate = getLineRate(line, ratio);

  const supportName = line?.ndis_support_item_name;
  const itemCode = line?.ndis_support_item_number;
  const cancelled = parseBooleanVal(item?.invoice_cancelled);

  return (
    <div className="rib-line-row" role="row">
      <div className="rib-line-time" role="cell">
        <span className="rib-line-date">{schedule.date}</span>
        {schedule.hours ? (
          <span className="rib-line-hours">{schedule.hours}</span>
        ) : null}
      </div>

      <div className="rib-line-service" role="cell">
        {basic ? (
          <>
            <p className="rib-line-service-empty">No service item allocated</p>
            <span className="rib-line-badge">Basic shift</span>
          </>
        ) : (
          <>
            {showClaimTypeScheduleTimesheets && supportName ? (
              <p className="rib-line-service-name">
                {supportName}
                {formatBreakdownClaimTypeSuffix(line)}
              </p>
            ) : !showClaimTypeScheduleTimesheets && supportName ? (
              <p className="rib-line-service-name">{supportName}</p>
            ) : (
              <p className="rib-line-service-empty">—</p>
            )}
          </>
        )}
      </div>

      <div className="rib-line-code" role="cell">
        {itemCode || "—"}
        {cancelled ? " (Cancelled)" : ""}
      </div>

      <div className="rib-line-num rib-line-ratio" role="cell">
        {ratio}
      </div>

      <div
        className={`rib-line-num${quantity == null ? " rib-line-num--muted" : ""}`}
        role="cell"
      >
        {quantity ?? "—"}
      </div>

      <div
        className={`rib-line-num${rate == null ? " rib-line-num--muted" : ""}`}
        role="cell"
      >
        {formatMoney(rate) ?? "—"}
      </div>

      <div
        className={`rib-line-num rib-line-num--total${total == null ? " rib-line-num--muted" : ""}`}
        role="cell"
      >
        {formatMoney(total) ?? "—"}
      </div>
    </div>
  );
});

const ParticipantBlock = memo(({
  group,
  item,
  showClaimTypeScheduleTimesheets,
  index,
  total,
  showHeader,
}) => {
  const subtotal = useMemo(
    () => sumGroupTotal(group.lines, item),
    [group.lines, item]
  );

  return (
    <div className="rib-participant-block">
      {showHeader ? (
        <div className="rib-participant-head">
          <div className="rib-participant-avatar" aria-hidden>
            {group.initials}
          </div>
          <div className="rib-participant-info">
            <p className="rib-participant-eyebrow">
              Participant {index + 1} of {total}
              {group.isShared ? " · shared" : ""}
            </p>
            <p className="rib-participant-name" title={group.participantLabel}>
              {capitalizeWords(group.participantLabel)}
            </p>
          </div>
          <div className="rib-participant-subtotal">
            <span className="rib-participant-subtotal-label">Subtotal</span>
            <span className="rib-participant-subtotal-value">
              {formatMoney(subtotal) ?? "—"}
            </span>
          </div>
        </div>
      ) : null}

      {group.lines.map(({ line, lineIndex }) => (
        <LineRow
          key={line?.uuid ?? `line-${lineIndex}`}
          line={line}
          lineIndex={lineIndex}
          item={item}
          showClaimTypeScheduleTimesheets={showClaimTypeScheduleTimesheets}
        />
      ))}
    </div>
  );
});

const TravelSection = memo(({ item, travelRow = null }) => {
  const isV5Travel = Boolean(travelRow);
  const km = parseFloat(isV5Travel ? travelRow?.km : item?.km) || 0;
  const useAltRate = isV5Travel
    ? travelRow?.km_alternative_rate
    : item?.km_alternative_rate;
  const selectedTravelItem = isV5Travel
    ? travelRow?.travel_items?.find(
        (t) => String(t?.id) === String(travelRow?.km_item_id)
      ) ||
      travelRow?.travel_item ||
      travelRow?.suggested_travel_item ||
      null
    : null;
  const price = isV5Travel
    ? parseFloat(travelRow?.unit_price ?? travelRow?.km_price) ||
      parseFloat(selectedTravelItem?.price) ||
      0
    : useAltRate
      ? parseFloat(item?.km_price) || 0
      : parseFloat(item?.travel_item?.price) || 0;

  const total = isV5Travel
    ? travelShareAmount(travelRow)
    : truncateDecimal(km * price);

  // Share-based KM: unit rate is full catalogue/alternate price (amount = km × rate).
  const displayRate = price;

  // Prefer the travel support-item code from the API for each opted-in participant.
  const travelCode = isV5Travel
    ? travelRow?.travel_item_number ||
      travelRow?.support_item_number ||
      selectedTravelItem?.support_item_number ||
      travelRow?.km_support_item_number ||
      (travelRow?.default_km_code != null && travelRow.default_km_code !== ""
        ? travelRow.default_km_code
        : null) ||
      null
    : item?.travel_item?.support_item_number || null;

  const serviceLabel = isV5Travel
    ? travelCode || "—"
    : travelCode
      ? `${travelCode}${
          item?.travel_item?.support_item_name
            ? ` · ${item.travel_item.support_item_name}`
            : ""
        }`
      : "—";

  const titleSuffix = isV5Travel
    ? getParticipantDisplayName(travelRow) || travelRow?.participant_name
    : null;

  return (
    <section className="rib-travel">
      <div className="rib-travel-head">
        <span className="rib-travel-icon" aria-hidden>
          <HiOutlineTruck className="h-4 w-4" />
        </span>
        <h5 className="rib-travel-title">
          Travel charges{titleSuffix ? ` · ${titleSuffix}` : ""}
        </h5>
      </div>
      <div className="rib-travel-body rib-travel-body--with-rate">
        <div>
          <p className="rib-travel-field-label">Date</p>
          <p className="rib-travel-field-value">
            {item?.start_time
              ? moment(item.start_time).format("ddd, D MMM YY · hh:mm A")
              : "—"}
          </p>
        </div>
        <div>
          <p className="rib-travel-field-label">
            {isV5Travel ? "Service item code" : "Service item"}
          </p>
          <p className="rib-travel-field-value">{serviceLabel}</p>
        </div>
        <div>
          <p className="rib-travel-field-label">Distance</p>
          <p className="rib-travel-field-value">{km ? `${km} km` : "—"}</p>
        </div>
        <div>
          <p className="rib-travel-field-label">Rate</p>
          <p className="rib-travel-field-value">
            {displayRate ? formatMoney(displayRate) : "—"}
          </p>
        </div>
        <div>
          <p className="rib-travel-field-label">Total</p>
          <p className="rib-travel-field-value rib-travel-field-value--total">
            {formatMoney(total) ?? "—"}
          </p>
        </div>
      </div>
    </section>
  );
});

const RosterInvoiceBreakdownPanel = ({
  item,
  showClaimTypeScheduleTimesheets = true,
  shiftTotal,
}) => {
  const visibleLines = useMemo(() => getVisibleInvoiceLines(item), [item]);
  const participantGroups = useMemo(
    () => groupInvoiceLinesByParticipant(item, visibleLines),
    [item, visibleLines]
  );
  const hasMultipleParticipants = (item?.participants?.length ?? 0) > 1;
  const showParticipantHeaders = hasMultipleParticipants;
  const isV5 = isV5MultiAssignmentTimesheet(item);
  const participantTravel = useMemo(
    () => getParticipantTravelRows(item),
    [item]
  );

  const lineGroups = useMemo(() => {
    if (participantGroups.length > 0) return participantGroups;
    return [
      {
        participantKey: "all",
        participantLabel: "",
        initials: "—",
        participant: item?.participants?.[0] ?? null,
        lines: visibleLines.map((line, lineIndex) => ({ line, lineIndex })),
        isShared: false,
      },
    ];
  }, [participantGroups, visibleLines, item?.participants]);

  const participantCount = item?.participants?.length ?? 0;

  const optedInTravelRows = useMemo(
    () => participantTravel.filter(isKmOptedIn),
    [participantTravel]
  );

  const showLegacyTravel = !isV5 && Boolean(item?.travel_item);
  const showV5TravelSummary =
    isV5 &&
    optedInTravelRows.length > 0 &&
    (item?.km_total != null ||
      optedInTravelRows.some((row) => parseFloat(row?.km) > 0));

  return (
    <div className="rib-panel">
      <div className="rib-panel-toolbar">
        <div>
          <h4 className="rib-panel-title">Billable breakdown</h4>
          <p className="rib-panel-subtitle">
            {showParticipantHeaders
              ? `${participantCount} participants · each section is billed separately`
              : participantCount === 1
                ? "Review allocated service items for this shift"
                : "Review line items included on this invoice"}
          </p>
        </div>
        <div className="rib-panel-meta">
          {visibleLines.length > 0 ? (
            <span className="rib-chip">
              {visibleLines.length} line{visibleLines.length !== 1 ? "s" : ""}
            </span>
          ) : null}
          {shiftTotal != null ? (
            <span className="rib-chip rib-chip--total">
              Shift total {formatMoney(shiftTotal)}
            </span>
          ) : null}
          {showV5TravelSummary && item?.km_total != null ? (
            <span className="rib-chip">
              Travel {formatMoney(item.km_total)}
            </span>
          ) : null}
        </div>
      </div>

      {visibleLines.length === 0 ? (
        <div className="rib-empty">
          <div className="rib-empty-icon" aria-hidden>
            <HiOutlineClipboardDocumentList className="h-5 w-5" />
          </div>
          <p className="rib-empty-title">No billable lines</p>
          <p className="rib-empty-text">
            This shift has no breakdown items ready for invoicing.
          </p>
        </div>
      ) : (
        <div className="rib-breakdown" role="table">
          <GridHead />
          {lineGroups.map((group, index) => {
            const travelRow = isV5
              ? findParticipantTravelForParticipant(
                  participantTravel,
                  group.participant
                )
              : null;
            const showGroupTravel =
              travelRow &&
              isKmOptedIn(travelRow) &&
              (parseFloat(travelRow?.km) > 0 ||
                travelRow?.travel_amount_preview != null);

            return (
              <React.Fragment key={group.participantKey}>
                <ParticipantBlock
                  group={group}
                  item={item}
                  showClaimTypeScheduleTimesheets={showClaimTypeScheduleTimesheets}
                  index={index}
                  total={lineGroups.length}
                  showHeader={showParticipantHeaders}
                />
                {showGroupTravel ? (
                  <TravelSection item={item} travelRow={travelRow} />
                ) : null}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {showLegacyTravel ? <TravelSection item={item} /> : null}
    </div>
  );
};

export default memo(RosterInvoiceBreakdownPanel);
